<div class="d-flex flex-wrap justify-content-between align-items-center mb-4">
  <div>
    <h5 class="mb-1 fw-bold">Warehouse Stock</h5>
    <span class="sub-text">Finished goods received at warehouses from factory transfers</span>
  </div>

  <div class="d-flex gap-2 align-items-center flex-wrap">
    <ng-select
      placeholder="All Warehouses"
      [items]="warehouses"
      [(ngModel)]="selectedWarehouse"
      bindValue="id"
      bindLabel="name"
      [clearable]="true"
      [searchable]="false"
      (ngModelChange)="onWarehouseChange()"
      style="min-width: 200px" />
    <button
      class="btn"
      [class.btn-primary]="showBatchView"
      [class.btn-soft-primary]="!showBatchView"
      (click)="toggleBatchView()">
      <i class="bi bi-layers"></i>
      {{ showBatchView ? 'Hide Batches' : 'Show Batches' }}
    </button>
    <button
      class="btn btn-soft-primary btn-sm d-flex align-items-center gap-2"
      (click)="exportStock()"
      [disabled]="isExporting">
      @if (isExporting) {
        <span class="spinner-border spinner-border-sm"></span>
      } @else {
        <i class="bi bi-download"></i>
      }
      Export
    </button>
  </div>
</div>

<div class="alert alert-info d-flex align-items-start mb-3" role="alert">
  <i class="bi bi-info-circle-fill me-2 mt-1"></i>
  <div>
    <strong>Stock here arrives by transfer from a factory.</strong>
    Where a physical count disagrees with the system, correct it with Adjust on a batch row under Show Batches. An
    adjustment cannot be edited or deleted: a wrong one is undone by posting its opposite, and both stay in the ledger.
    Adjusting a batch to zero empties the row and drops it off this list. A batch already at zero does not appear here,
    so adjust it from the Production Entries tab instead.
  </div>
</div>

@if (isDataLoading) {
  <app-loading-spinner></app-loading-spinner>
} @else {
  @if (!showBatchView) {
    @if (stockItems.length === 0) {
      <app-info-alert>
        No warehouse stock found.
        @if (!selectedWarehouse) {
          Select a specific warehouse or initiate product transfers to populate warehouse stock.
        }
      </app-info-alert>
    } @else {
      <div class="table-responsive">
        <table class="table table-hover align-middle mb-0">
          <thead class="bg-light">
            <tr>
              <th class="border-0 text-uppercase small fw-bold text-muted py-3">Product</th>
              <th class="border-0 text-uppercase small fw-bold text-muted py-3">Packaging</th>
              <th class="border-0 text-uppercase small fw-bold text-muted py-3">Warehouse</th>
              <th class="border-0 text-uppercase small fw-bold text-muted py-3 text-center">Available</th>
              <th class="border-0 text-uppercase small fw-bold text-muted py-3 text-center">Total Stock Cost</th>
              <th class="border-0 text-uppercase small fw-bold text-muted py-3 text-center">Last Updated</th>
            </tr>
          </thead>
          <tbody>
            @for (item of stockItems; track item.id) {
              <tr>
                <td class="py-3 fw-semibold">{{ item.product_name }}</td>
                <td class="py-3 text-muted small">
                  {{ item.packing_size }} {{ item.unit }} / {{ item.packaging_type }}
                </td>
                <td class="py-3">
                  <span class="badge app-badge badge-soft-success">{{ item.warehouse_name | titlecase }}</span>
                </td>
                <td class="py-3 text-center">
                  <span
                    class="fw-bold font-monospace"
                    [class.text-success]="+item.boxes_available > 0"
                    [class.text-danger]="+item.boxes_available <= 0">
                    {{ item.boxes_available }} boxes
                  </span>
                  <div class="text-muted small font-monospace">({{ item.units_available }} nos)</div>
                </td>
                <td class="py-3 text-center text-muted small">
                  {{ item.total_stock_cost | currency: 'INR' }}
                </td>
                <td class="py-3 text-center text-muted small">
                  {{ item.updated_at | date: 'mediumDate' }}
                </td>
              </tr>
            }
          </tbody>
          @if (totalStockValue != null) {
            <tfoot>
              <tr class="table-light border-top">
                <td colspan="4" class="py-2 pe-3 text-end text-muted small fw-semibold">
                  Total Stock Value (all pages)
                </td>
                <td class="py-2 text-center fw-bold small">
                  {{ totalStockValue | currency: 'INR' : 'symbol' : '1.2-2' }}
                </td>
                <td></td>
              </tr>
            </tfoot>
          }
        </table>
      </div>

      @if (stockItems.length > 0) {
        <div class="mt-4">
          <app-pagination
            [currentPage]="currentPage"
            [totalCount]="totalCount"
            [pageSize]="10"
            [hasNextPage]="hasNextPage"
            [hasPreviousPage]="hasPreviousPage"
            (pageChange)="onPageChange($event)"></app-pagination>
        </div>
      }
    }
  } @else {
    @if (batchStockItems.length === 0) {
      <app-info-alert>
        No batch stock found.
        @if (!selectedWarehouse) {
          Select a specific warehouse to view batch-level stock.
        }
      </app-info-alert>
    } @else {
      <div class="table-responsive">
        <table class="table table-hover align-middle mb-0">
          <thead class="bg-light">
            <tr>
              <th class="border-0 text-uppercase small fw-bold text-muted py-3">Product</th>
              <th class="border-0 text-uppercase small fw-bold text-muted py-3">Packaging</th>
              <th class="border-0 text-uppercase small fw-bold text-muted py-3">Warehouse</th>
              <th class="border-0 text-uppercase small fw-bold text-muted py-3">Batch No.</th>
              <th class="border-0 text-uppercase small fw-bold text-muted py-3">Mfg Date</th>
              <th class="border-0 text-uppercase small fw-bold text-muted py-3">Exp Date</th>
              <th class="border-0 text-uppercase small fw-bold text-muted py-3 text-center">Available</th>
              <th class="border-0 text-uppercase small fw-bold text-muted py-3 text-center">Total Stock Cost</th>
              <th class="border-0 text-uppercase small fw-bold text-muted py-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            @for (group of groupedBatchStock; track group.product_name) {
              @for (row of group.rows; track $index; let isFirst = $first) {
                <tr>
                  @if (isFirst) {
                    <td class="py-3 fw-semibold align-middle" [attr.rowspan]="group.rows.length + 1">
                      {{ group.product_name }}
                    </td>
                  }
                  <td class="py-3 text-muted small">{{ row.packaging }}</td>
                  <td class="py-3">
                    <span class="badge app-badge badge-soft-success">{{ row.warehouse_name | titlecase }}</span>
                  </td>
                  <td class="py-3 font-monospace small fw-semibold">{{ row.batch_number }}</td>
                  <td class="py-3 text-muted small">
                    {{ row.mfg_date !== '-' ? (row.mfg_date | date: 'mediumDate') : '—' }}
                  </td>
                  <td class="py-3 text-muted small">
                    {{ row.exp_date !== '-' ? (row.exp_date | date: 'mediumDate') : '—' }}
                  </td>
                  <td class="py-3 text-center">
                    <span
                      class="fw-bold font-monospace"
                      [class.text-success]="+row.boxes_available > 0"
                      [class.text-danger]="+row.boxes_available <= 0">
                      {{ row.boxes_available }} boxes
                    </span>
                    <div class="text-muted small font-monospace">({{ row.units_available }} nos)</div>
                  </td>
                  <td class="py-3 text-center text-muted small">{{ row.total_stock_cost | currency: 'INR' }}</td>
                  <td class="py-3 text-center text-nowrap">
                    <button
                      type="button"
                      class="btn btn-soft-primary btn-sm me-1"
                      title="Adjust this batch's stock in this warehouse"
                      (click)="openAdjustModal(row.source, adjustModal)">
                      <i class="bi bi-sliders"></i>
                    </button>
                    <button
                      type="button"
                      class="btn btn-soft-primary btn-sm"
                      title="Adjustment history for this batch"
                      (click)="openHistoryModal(row.source, historyModal)">
                      <i class="bi bi-clock-history"></i>
                    </button>
                  </td>
                </tr>
              }
              <!-- Per-product total row -->
              <tr class="table-light border-top">
                <td colspan="6" class="py-2 pe-3 text-end text-muted small fw-semibold">Total Cost</td>
                <td class="py-2 text-center fw-bold small">{{ group.total_cost | currency: 'INR' }}</td>
                <td></td>
              </tr>
            }
          </tbody>
          @if (totalStockValue != null) {
            <tfoot>
              <tr class="table-light border-top">
                <td colspan="7" class="py-2 pe-3 text-end text-muted small fw-semibold">
                  Total Stock Value (all pages)
                </td>
                <td class="py-2 text-center fw-bold small">
                  {{ totalStockValue | currency: 'INR' : 'symbol' : '1.2-2' }}
                </td>
                <td></td>
              </tr>
            </tfoot>
          }
        </table>
      </div>

      @if (groupedBatchStock.length > 0) {
        <div class="mt-4">
          <app-pagination
            [currentPage]="currentPage"
            [totalCount]="totalCount"
            [pageSize]="10"
            [hasNextPage]="hasNextPage"
            [hasPreviousPage]="hasPreviousPage"
            (pageChange)="onPageChange($event)"></app-pagination>
        </div>
      }
    }
  }
}

<!-- Adjust warehouse stock for this batch -->
<ng-template #adjustModal>
  <div class="modal-header">
    <h5 class="modal-title fw-bold">Adjust Warehouse Stock</h5>
    <button type="button" class="btn-close" aria-label="Close" (click)="onAdjustCancelled()"></button>
  </div>

  @if (adjustContext) {
    <app-product-stock-adjustment-form
      [context]="adjustContext"
      (saved)="onAdjustmentSaved()"
      (cancelled)="onAdjustCancelled()"></app-product-stock-adjustment-form>
  }
</ng-template>

<!-- Adjustment history for one batch in one warehouse -->
<ng-template #historyModal let-modal>
  <div class="modal-header">
    <div>
      <h5 class="modal-title fw-bold">Adjustment History</h5>
      @if (historyBatch) {
        <span class="text-muted small">
          Batch {{ historyBatch.batch_number }} &middot; {{ historyBatch.product_name }} &middot;
          {{ historyBatch.warehouse_name }}
        </span>
      }
    </div>
    <button type="button" class="btn-close" aria-label="Close" (click)="modal.dismiss()"></button>
  </div>

  <div class="modal-body p-4">
    @if (historyBatch) {
      <app-product-stock-adjustments
        scope="warehouse"
        [productionEntry]="historyBatch.production_entry"
        [warehouse]="historyBatch.warehouse"
        [refreshToken]="historyRefreshToken"></app-product-stock-adjustments>
    }
  </div>
</ng-template>
